import { Helmet } from "react-helmet-async";
import { useT } from "../../contexts/LanguageContext";

/**
 * Per-page SEO + Open Graph + Twitter Card meta tags + Schema.org JSON-LD.
 * Usage: <SEOHead pageKey="dashboard" /> at the top of any page component.
 *
 * Extra schemas may be provided via the `jsonLd` prop (single object or array).
 * When present, they are appended to the default Organization schema.
 */
export default function SEOHead({ pageKey = "default", path = "", image = null, jsonLd = null }) {
  const { t, lang } = useT();

  const siteName = "PMCoach AI";
  const baseUrl = (typeof window !== "undefined" && window.location.origin) || "https://pmcoach-ai.com";
  const currentPath = path || (typeof window !== "undefined" ? window.location.pathname : "");
  const url = `${baseUrl}${currentPath}`;

  const title = `${t(`seo.${pageKey}.title`)} — ${siteName}`;
  const description = t(`seo.${pageKey}.description`);
  const keywords = t(`seo.${pageKey}.keywords`);
  // For social share unfurls (LinkedIn/Slack/WhatsApp) we ALWAYS want an
  // absolute production URL — even when the app is running on preview/localhost.
  const PROD_BASE = "https://pmcoach-ai.com";
  const ogImage = image || `${PROD_BASE}/og-default.png`;

  // Alternate-language URL: swap the ?lang= param for EN, root path for TR.
  const trUrl = `${baseUrl}${currentPath}`;
  const enUrl = `${baseUrl}${currentPath}${currentPath.includes("?") ? "&" : "?"}lang=en`;

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    name: siteName,
    url: baseUrl,
    logo: `${baseUrl}/logo-full.png`,
    sameAs: [
      "https://www.linkedin.com/company/pmcoach-ai",
      "https://twitter.com/pmcoach_ai",
    ],
    description,
  };

  const extraJsonLd = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />

      {/* hreflang alternates */}
      <link rel="alternate" hrefLang="tr" href={trUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={trUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={lang === "tr" ? "tr_TR" : "en_US"} />
      <meta property="og:locale:alternate" content={lang === "tr" ? "en_US" : "tr_TR"} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured data — Organization (always emitted) */}
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>

      {/* Extra structured data supplied by the page (Course, Product, FAQPage, BlogPosting…) */}
      {extraJsonLd.map((ld, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
      ))}
    </Helmet>
  );
}
